import { useEffect } from 'react';
import { CheckCircle2, WifiOff } from '@/lib/icons';
import { cn } from '@/lib/utils';
import { startBackendStatusWatcher, useBackendStatus } from '@/lib/api/backend-status';
import { Button } from '@/components/ui/button';

/**
 * "Reconnecting to Dreamwell" — driven only by the health poller, so it
 * appears for a real outage (a deploy window, no network) and never for one
 * slow request. Offers "Try now", and says so briefly when we're back.
 *
 * CSS transitions only: this mounts at the root, so it is in the entry chunk
 * every page pays for (the sign-in page included).
 */
export function ReconnectBanner() {
  const status = useBackendStatus((s) => s.status);
  const checkNow = useBackendStatus((s) => s.checkNow);
  useEffect(() => startBackendStatusWatcher(), []);

  const visible = status !== 'online';
  const recovered = status === 'recovered';
  return (
    <div aria-live="polite" className="pointer-events-none fixed inset-x-0 top-4 z-50 flex justify-center px-4">
      <div
        role="status"
        aria-hidden={!visible}
        className={cn(
          'flex items-center gap-3 rounded-full bg-menu py-2 pl-3.5 pr-2 text-[13.5px] text-ink shadow-menu transition-[opacity,transform] duration-300 ease-studio',
          visible ? 'pointer-events-auto translate-y-0 opacity-100' : '-translate-y-3 opacity-0',
        )}
      >
        {recovered ? <CheckCircle2 className="size-4 text-green-fg" /> : <WifiOff className="size-4 text-amber-fg" />}
        <span className="font-medium">{recovered ? 'Back online' : status === 'offline' ? "You're offline" : 'Reconnecting to Dreamwell…'}</span>
        {!recovered && visible ? (
          <Button variant="ghost" size="sm" onClick={checkNow} className="h-auto px-3 py-1 text-link">
            Try now
          </Button>
        ) : (
          <span className="pr-2" />
        )}
      </div>
    </div>
  );
}
